import React from 'react';
import { styled } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { Colors } from 'library';
import { BaseButton } from './BaseButton';

interface SplitPillButtonProps {
    children: React.ReactNode;
    href: string;
    onCopy: () => void;
    variant?: 'primary' | 'secondary';
    icon?: React.ReactNode;
}

export function SplitPillButton({ children, href, onCopy, variant = 'primary', icon }: SplitPillButtonProps) {
    const isPrimary = variant === 'primary';
    return (
        <Container $isPrimary={isPrimary}>
            <Action href={href} $isPrimary={isPrimary}>
                {icon}
                {children}
            </Action>
            <Divider $isPrimary={isPrimary} />
            <CopyButton onClick={onCopy} $isPrimary={isPrimary} aria-label="Copy">
                <ContentCopy sx={{ fontSize: 16 }} />
            </CopyButton>
        </Container>
    );
}

const baseSegment = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    fontSize: 14,
    fontWeight: 600,
    textDecoration: 'none',
    transition: 'background-color 0.15s ease',
};

const Container = styled('div')<{ $isPrimary: boolean }>(({ $isPrimary }) => ({
    display: 'inline-flex',
    alignItems: 'stretch',
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: $isPrimary ? Colors.darkNavy : Colors.white,
    border: $isPrimary ? 'none' : `1px solid ${Colors.lightGrey}`,
    color: $isPrimary ? Colors.white : Colors.darkNavy,
}));

const Action = styled('a')<{ $isPrimary: boolean }>(({ $isPrimary }) => ({
    ...baseSegment,
    padding: '8px 20px',
    color: 'inherit',
    '&:hover': {
        backgroundColor: $isPrimary ? Colors.primaryDarkened : Colors.fill,
        color: 'inherit',
        textDecoration: 'none',
    },
}));

const Divider = styled('div')<{ $isPrimary: boolean }>(({ $isPrimary }) => ({
    width: 1,
    backgroundColor: $isPrimary ? Colors.primaryDarkened : Colors.border,
}));

const CopyButton = styled(BaseButton)<{ $isPrimary: boolean }>(({ $isPrimary }) => ({
    ...baseSegment,
    padding: '0 12px',
    color: 'inherit',
    backgroundColor: 'transparent',
    '&:hover': {
        backgroundColor: $isPrimary ? Colors.primaryDarkened : Colors.fill,
    },
}));
